"use client";

import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppSelector } from "@/hooks/useAppSelector";
import { getBrandNavigationData } from "@/utils/api";
import {
  BadgeCheck,
  PackageCheck,
  ShoppingCart,
  Sparkles,
  ChevronRight,
  MoveRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatWithCommasWithDecimal } from "@/utils/helpers";

interface MobileBuildYourCartProps {
  expanded: boolean;
}

const MobileBuildYourCart: React.FC<MobileBuildYourCartProps> = ({
  expanded,
}) => {
  const { cart } = useAppSelector((state) => state.cart);
  const { selectedBrand } = useAppSelector((state) => state.steps);
  const { manufacturers } = useAppSelector((state) => state.home);

  const brandData = useMemo(
    () => getBrandNavigationData(cart, selectedBrand, manufacturers),
    [cart, selectedBrand, manufacturers]
  );

  return (
    <div className="w-full z-50">
      {/* Animated Banner Header */}
      <div
        className={`bg-[#c92a2a] text-white-1 px-4 py-1 ${
          expanded ? "py-2" : "pb-[50px]"
        } flex items-center justify-center rounded-t-lg shadow-md`}
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="h-4 w-4 animate-bounce text-gold/90" />
          Additional Discounts are waiting
          <Sparkles className="h-4 w-4 animate-bounce text-gold/90" />
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white-1 px-4 py-4 text-sm space-y-4 border-t pb-[50px]"
          >
            <ProgressSection
              label="Cart Progress"
              value={brandData.BeforeMOQ}
              color="bg-red-600"
              bgColor="bg-red-100"
              icon={<ShoppingCart className="h-4 w-4 text-red-600" />}
              labelText={`Current Cart Value: ${formatWithCommasWithDecimal(brandData?.totalBrandCartValue)}`}
              nextText={brandData.MOQState?.level1 || "Reach MOQ for Discounts"}
            />

            <ProgressSection
              label="MOQ Progress"
              value={brandData.MOQProgress}
              color="bg-blue-700"
              bgColor="bg-blue-100"
              icon={<PackageCheck className="h-4 w-4 text-blue-700" />}
              labelText={brandData.MOQState?.level1 || "MOQ Target"}
              nextText={
                brandData.DiscountState?.level2
              }
            />

            {brandData.discountPrice && (
              <ProgressSection
                label="Discount Progress"
                value={brandData.DiscountProgress}
                color="bg-green-600"
                bgColor="bg-green-100"
                icon={<BadgeCheck className="h-4 w-4 text-green-600" />}
                labelText={
                  brandData.DiscountState?.level2 || `${brandData?.DiscountState?.level1}`
                }
                nextText=""
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface ProgressSectionProps {
  label: string;
  value: number;
  color: string;
  bgColor: string;
  icon: React.ReactNode;
  labelText: string;
  nextText: string;
}

const ProgressSection: React.FC<ProgressSectionProps> = ({
  label,
  value,
  color,
  bgColor,
  icon,
  labelText,
  nextText,
}) => {
  const percentage = Math.min(100, value * 100);

  return (
    <div className="space-y-3 rounded-lg border border-muted p-4 shadow-sm bg-white">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <div className="text-primary">{icon}</div>
          <span>{label}</span>
        </div>
        <span className="text-xs text-muted-foreground font-medium">
          {Math.round(percentage)}%
        </span>
      </div>

      {/* Progress Bar */}
      <div className={cn("relative w-full h-3 rounded-full overflow-hidden", bgColor)}>
        {/* Filled Section */}
        <motion.div
          className={cn("h-full", color, "relative")}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
        >
          {/* Lighting shimmer on top of fill */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="w-1/3 h-full bg-white/30 animate-light-glide rounded-full" />
          </div>
        </motion.div>
      </div>

      {/* Descriptive Labels */}
      <div className="space-y-1 mt-1">
        <div className="flex items-center text-xs text-gray-700 font-medium gap-1">
          <ChevronRight className="w-3 h-3 text-gray-500" />
          <span>{labelText}</span>
        </div>
        {nextText && (
          <div className="flex items-center text-xs text-gray-500 gap-1">
            <MoveRight className="w-3 h-3 text-gray-400" />
            <span>{nextText}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileBuildYourCart;
